import React, { useEffect, useMemo, useState } from 'react';
import DataTable, { TableColumn } from 'react-data-table-component';
import { Card, CardBody } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { get } from '../../../hook/request';
import { Btn, H3 } from '../../../AbstractElements';
import DeleteConfirm from '../../../Components/Modals/Delete';
import Loader from '../../../Components/Widgets/Loader/generic';

type RawUser = {
  ID: number;
  ragionesociale?: string | null;
  nome: string;
  cognome: string;
  email: string;
  telefono?: string | null;
};

export default function UsersList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<RawUser[]>([]);
  const [loading, setLoading] = useState(false);

  const [filterText, setFilterText] = useState('');

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await get<any>('/clienti/list');
      const payload = (res as any)?.data ?? res;
      const list: RawUser[] = Array.isArray(payload) ? payload : [];
      setRows(list);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadUsers();
  }, []);

  const columns: TableColumn<RawUser>[] = useMemo(
    () => [
      {
        name: 'Ragione sociale',
        selector: r => r.ragionesociale ?? '',
        sortable: true,
        grow: 1.8,
        cell: r => r.ragionesociale || '-',
      },
      {
        name: 'Nominativo',
        selector: r => `${r.cognome ?? ''} ${r.nome ?? ''}`.trim(),
        sortable: true,
        grow: 1.6,
        cell: r => {
          const full = `${r.cognome ?? ''} ${r.nome ?? ''}`.trim();
          return full || '-';
        },
      },
      {
        name: 'Email',
        selector: r => r.email ?? '',
        sortable: true,
        grow: 1.6,
        cell: r => r.email || '-',
      },
      {
        name: 'Telefono',
        selector: r => r.telefono ?? '',
        sortable: true,
        grow: 1.2,
        cell: r => r.telefono || '-',
      },
      {
        name: 'Azioni',
        width: '200px',
        ignoreRowClick: true,
        cell: r => {
          return (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={e => {
                  e.stopPropagation(); 
                  navigate(`/gestione-clienti/${encodeURIComponent(String(r.ID))}`);
                }}
                title="Modifica"
              >
                Modifica
              </button>
              <DeleteConfirm
                url={`/clienti?ID=${r.ID}`}
                onSuccess={() => {
                  loadUsers();
                }}
                trigger={
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    title="Elimina"
                    
                  >
                    Elimina
                  </button>
                }
              />
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  // righe filtrate
  const filteredRows = useMemo(() => {
    const term = filterText.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter(r => {
      const ragione = r.ragionesociale?.toLowerCase() ?? '';
      const nome = r.nome?.toLowerCase() ?? '';
      const cognome = r.cognome?.toLowerCase() ?? '';
      const email = r.email?.toLowerCase() ?? '';
      const telefono = r.telefono?.toLowerCase() ?? '';
      const nominativo = `${cognome} ${nome}`.trim();

      return (
        ragione.includes(term) ||
        nominativo.includes(term) ||
        email.includes(term) ||
        telefono.includes(term)
      );
    });
  }, [rows, filterText]);

  return (
    <div className="page-body pt-4">
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <H3 className="mb-0">Clienti</H3>
            <Btn
              className="px-4"
              color="primary"
              type="button"
              onClick={() => navigate('/gestione-clienti/new')}
            >
              NUOVO
            </Btn>
          </div>

          {/* Filtro ricerca */}
          <div className="mb-3 d-flex justify-content-between">
            <input
              type="text"
              className="form-control w-50"
              placeholder="Filtra per ragione sociale, nominativo, email o telefono..."
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
            />
          </div>

          <div className="table-responsive">
            <DataTable
              columns={columns}
              data={filteredRows}
              progressPending={loading}
              progressComponent={<Loader />}
              striped
              pagination
              highlightOnHover
              pointerOnHover
              dense
              noDataComponent="Nessun utente disponibile"
              paginationPerPage={50}
              onRowClicked={r =>
                navigate(`/gestione-clienti/${encodeURIComponent(String(r.ID))}`)
              }
              customStyles={{
                rows: {
                  style: {
                    minHeight: '30px',
                    paddingTop: '5px',
                    paddingBottom: '5px',
                    cursor: 'pointer',
                  },
                },
                cells: {
                  style: {
                    padding: '4px 10px',
                  },
                },
              }}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
